import {
  TopicsWithId,
  options,
  topicsId,
} from "@/components/one-to-one/Constants.tsx";
import SelectTutor from "@/components/one-to-one/SelectTutor.tsx";
import routes from "@/config/routes.ts";
import { useBookOneToOneMutation } from "@/data/one-to-one";
import { useMe } from "@/data/user";
import OneToOneSectionLayout from "@/layouts/OneToOneSectionLayout.tsx";
import useOneToOne from "@/utils/hooks/useOneToOne";
import { cn } from "@edusogno/lib/utils";
import Button from "@edusogno/primitives/button";
import LoadingButton from "@edusogno/primitives/loading-button.js";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@edusogno/primitives/toggle-group";
import dayjs from "dayjs";
import { SetStateAction } from "jotai";
import { Dispatch, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface DesktopSecondSessionViewProps {
  topicsDone: string[] | null;
  setSecondSession: Dispatch<SetStateAction<boolean>>;
  credits: number;
}

const DesktopSecondSessionView = ({
  topicsDone,
  setSecondSession,
  credits,
}: DesktopSecondSessionViewProps) => {
  const {
    date,
    hour,
    category,
    freeTopic,
    topic,
    tutor,
    selectedPreviousTutor,
    setState,
  } = useOneToOne();

  const { t } = useTranslation();

  const { me } = useMe();

  const navigate = useNavigate();

  const { mutateAsync, isError, isLoading } = useBookOneToOneMutation(
    () => null,
  );

  const [step, setStep] = useState<number>(selectedPreviousTutor ? 1 : 0);

  const [topicSelected, setTopicSelected] = useState<Array<{
    title: string;
    complete: number;
  }> | null>(null);

  const handleConfirm = async () => {
    if (!me) return;

    await mutateAsync({
      tutor_token: tutor,
      user_token: me.item.user.token,
      data: dayjs(date).format("YYYY-MM-DD"),
      ora: hour,
      ora_fine: dayjs(dayjs(date).format("YYYY-MM-DD") + " " + hour)
        .add(1, "hour")
        .format("HH:mm"),
      titolo_argomento: options[Number(category)]?.text,
      descrizione:
        topicsId[options[Number(category)]?.key as keyof TopicsWithId][
          Number(topic)
        ].title,
      id_material:
        topicsId[options[Number(category)]?.key as keyof TopicsWithId][
          Number(topic)
        ].id,
    });

    if (!isError) setStep(3);
  };

  const onBook = async () => {
    await handleConfirm();
  };

  useEffect(() => {
    const mappedTopics = topicsId[
      options[Number(category)]?.key as keyof TopicsWithId
    ]?.map((element: { title: string; complete: number }) => {
      if (topicsDone?.includes(element.title)) element.complete = 1;
      return element;
    });

    setTopicSelected(mappedTopics);
  }, [category, topicsDone]);

  return (
    <div className={"w-full h-[--content-height] flex gap-4"}>
      <div className={"h-full w-full rounded-md"}>
        <OneToOneSectionLayout isLastStep={step === 3}>
          {step === 0 && (
            <SelectTutor
              onBack={() => setSecondSession(false)}
              onNext={(tutor_token) => {
                setState("tutor", tutor_token);
                setStep((r) => r + 1);
              }}
            />
          )}
          {step === 1 && (
            <>
              <div className={"flex flex-col gap-1 w-full mb-4"}>
                <h2 className={"text-xl font-semibold text-secondary"}>
                  {t("one_to_one.choose_your_category.title")}
                </h2>
                <p className={"text-base text-secondary"}>
                  {t("one_to_one.choose_your_category.subtitle")}
                </p>
              </div>
              <ToggleGroup
                value={category}
                onValueChange={(el) => setState("category", el)}
                type={"single"}
                className={
                  "w-full gap-4 h-full grid auto-rows-[65px] grid-cols-3"
                }
              >
                {options?.map((r, idx) => (
                  <ToggleGroupItem
                    key={"option-" + idx}
                    className={
                      "rounded-md  h-full until-md:bg-[#E7DEFF] [--color:--secondary] [--icon-color:--primary] [--fw:400] data-[state=on]:[--color:white] data-[state=on]:[--fw:600] data-[state=off]:bg-[#EEEEEE] data-[state=on]:bg-[--primary] data-[state=on]:[--icon-color:white] [&_p]:font-[--fw] [&_p]:text-[--color] [&_svg]:text-[--icon-color] "
                    }
                    value={String(idx)}
                  >
                    <div
                      className={
                        "w-full grid grid-cols-[24px_auto] py-2 px-2 h-full  gap-4 items-center"
                      }
                    >
                      {r?.icon}
                      <p className={"text-xs text-start"}>{r?.text}</p>
                    </div>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </>
          )}
          {step === 2 && (
            <>
              <div className={"flex flex-col gap-1 w-full mb-4"}>
                <h2 className={"text-xl font-semibold text-secondary"}>
                  {t("one_to_one.choose_your_topic.title")}
                </h2>
                <p className={"text-base text-secondary"}>
                  {t("one_to_one.choose_your_topic.subtitle")}
                </p>
              </div>
              <ToggleGroup
                value={topic}
                onValueChange={(el) => setState("topic", el)}
                type={"single"}
                className={
                  "w-full items-stretch grid-cols-1 gap-[10px] h-full grid auto-rows-[36px]"
                }
              >
                {topicSelected?.map((r, idx) => (
                  <ToggleGroupItem
                    // data-alt={String(true)}
                    data-alt={String(r.complete == 1 && !freeTopic)}
                    disabled={r.complete == 1 && !freeTopic}
                    // disabled={true}
                    className={cn(
                      "rounded-[8px] relative pl-8 h-full bg-[#EEEEEE]  until-md:data-[alt=true]:data-[state=off]:bg-[#EEEEEE66]  until-md:data-[alt=true]:data-[state=off]:[--color:#2D224C66]  until-md:data-[alt=false]:disabled:[--color:#2D224C66] until-md:data-[alt=false]:disabled:bg-[#EEEEEE66]   md:data-[alt=false]:disabled:bg-[#FFFFFF33]    disabled:opacity-100   [--color:--secondary]  [--icon-opacity:0] [--fw:400] data-[state=on]:[--color:white] ] data-[state=on]:bg-[--primary] data-[state=on]:[--icon-opacity:100]  [&_p]:font-[--fw] [&_p]:text-[--color] [&>svg]:opacity-[--icon-opacity] ",
                      "md:data-[alt=true]:data-[state=off]:bg-[#8065C933] md:data-[alt=true]:data-[state=off]:[--color:#2D224C66]",
                    )}
                    key={"topic-" + idx}
                    value={String(idx)}
                  >
                    <p
                      className={
                        "w-full md:text-sm text-[12px] leading-[120%] text-start"
                      }
                    >
                      {r?.title}
                    </p>
                    <svg width="17" height="13" viewBox="0 0 17 13" fill="none">
                      <path
                        d="M5.19244 10.1924L1.69244 6.69244C1.60092 6.5998 1.49192 6.52625 1.37176 6.47605C1.25159 6.42585 1.12266 6.4 0.99244 6.4C0.862215 6.4 0.733284 6.42585 0.613124 6.47605C0.492963 6.52625 0.383962 6.5998 0.292439 6.69244C0.1998 6.78396 0.126248 6.89296 0.0760486 7.01312C0.025849 7.13328 0 7.26221 0 7.39244C0 7.52266 0.025849 7.65159 0.0760486 7.77175C0.126248 7.89191 0.1998 8.00092 0.292439 8.09244L4.48244 12.2824C4.87244 12.6724 5.50244 12.6724 5.89244 12.2824L16.4924 1.69244C16.5851 1.60092 16.6586 1.49192 16.7088 1.37175C16.759 1.25159 16.7849 1.12266 16.7849 0.992439C16.7849 0.862214 16.759 0.733284 16.7088 0.613124C16.6586 0.492963 16.5851 0.383962 16.4924 0.292439C16.4009 0.1998 16.2919 0.126248 16.1718 0.0760484C16.0516 0.0258488 15.9227 0 15.7924 0C15.6622 0 15.5333 0.0258488 15.4131 0.0760484C15.293 0.126248 15.184 0.1998 15.0924 0.292439L5.19244 10.1924Z"
                        fill="white"
                      />
                    </svg>
                    {!freeTopic && r.complete == 1 && (
                      <div
                        className={
                          " md:w-[131px] opacity-100 px-2 text-[12px] font-normal flex  flex-row items-center justify-between  h-5 shrink-0 border bg-[--green100] rounded-[5px] border-solid border-[--green100]"
                        }
                      >
                        <p className={"hidden md:block"}>
                          {t("one_to_one.topic.status.done")}
                        </p>

                        <p className={"md:hidden"}>
                          {t("one_to_one.topic.status.done_shorter")}
                        </p>
                        <svg
                          className={" until-md:hidden"}
                          width="19"
                          height="20"
                          viewBox="0 0 19 10"
                          fill="none"
                        >
                          <path
                            d="M13.63 0.239277C13.5555 0.165667 13.4669 0.107267 13.3694 0.0674215C13.2719 0.0275754 13.1674 0.00706525 13.0619 0.00706525C12.9564 0.00706525 12.8519 0.0275754 12.7544 0.0674215C12.6569 0.107267 12.5683 0.165667 12.4938 0.239277L7.9487 4.71766L9.08497 5.83725L13.63 1.35093C13.9363 1.0492 13.9363 0.541012 13.63 0.239277ZM17.0469 0.231337L9.08497 8.07644L6.28057 5.32113C6.12989 5.17266 5.92553 5.08925 5.71244 5.08925C5.49935 5.08925 5.29498 5.17266 5.1443 5.32113C4.99363 5.46959 4.90898 5.67096 4.90898 5.88092C4.90898 6.09089 4.99363 6.29225 5.1443 6.44072L8.51281 9.7598C8.82709 10.0695 9.33479 10.0695 9.64907 9.7598L18.1832 1.35887C18.2579 1.28541 18.3171 1.19816 18.3576 1.1021C18.398 1.00604 18.4188 0.903069 18.4188 0.799075C18.4188 0.695081 18.398 0.592108 18.3576 0.49605C18.3171 0.399993 18.2579 0.312737 18.1832 0.239277H18.1751C18.1024 0.164321 18.0151 0.104519 17.9185 0.0634198C17.8219 0.0223201 17.7179 0.000760338 17.6127 1.97509e-05C17.5075 -0.000720836 17.4032 0.0193727 17.306 0.0591083C17.2088 0.098844 17.1207 0.157412 17.0469 0.231337ZM0.591183 6.44866L3.95969 9.76774C4.27397 10.0774 4.78167 10.0774 5.09595 9.76774L5.66006 9.21192L1.72745 5.32113C1.6529 5.24752 1.56434 5.18912 1.46685 5.14927C1.36936 5.10942 1.26486 5.08891 1.15932 5.08891C1.05377 5.08891 0.949268 5.10942 0.85178 5.14927C0.754292 5.18912 0.665737 5.24752 0.591183 5.32113C0.276897 5.6308 0.276897 6.13899 0.591183 6.44866Z"
                            fill="#2D224C"
                          />
                        </svg>
                      </div>
                    )}
                    {freeTopic && (
                      <div
                        className={
                          " md:w-[131px] opacity-100 px-2 text-[12px] font-normal flex  flex-row items-center justify-between  h-5 shrink-0 border bg-[--green100] rounded-[5px] border-solid border-[--green100]"
                        }
                      >
                        <p className={"hidden md:block"}>free topic</p>
                        <p className={"md:hidden"}>free</p>
                        <svg
                          width="15px"
                          height="15px"
                          viewBox="0 0 0.45 0.45"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.225 0.075a0.06 0.06 0 0 0 -0.057 0.057v0.057H0.338a0.038 0.038 0 0 1 0.038 0.038v0.15a0.038 0.038 0 0 1 -0.038 0.038h-0.225A0.038 0.038 0 0 1 0.075 0.377v-0.15a0.038 0.038 0 0 1 0.038 -0.038h0.019V0.132C0.132 0.081 0.174 0.038 0.226 0.038s0.095 0.042 0.095 0.095a0.019 0.019 0 1 1 -0.038 0A0.06 0.06 0 0 0 0.226 0.075m-0.112 0.15v0.15h0.225V0.225z"
                            fill="#0D0D0D"
                          />
                        </svg>
                      </div>
                    )}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </>
          )}
          {step === 3 && (
            <>
              <div
                className={
                  "h-[218px] w-full bg-[#EEEEEE] rounded-[7px] px-6 flex justify-center gap-4 items-center flex-col"
                }
              >
                <div className={"flex flex-row  items-center w-full gap-6"}>
                  <svg
                    width="46"
                    height="46"
                    viewBox="0 0 46 46"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="23" cy="23" r="23" fill="#74DFAC" />
                    <path
                      d="M13.9062 28.1271L18.429 31.7895C18.7177 32.0232 19.0788 32.1274 19.437 32.0804C19.7952 32.0334 20.1227 31.8388 20.3511 31.5373L32.0923 16.0449"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                  <p className={"text-secondary text-2xl font-semibold my-4"}>
                    {t("one_to_one.event_created_successfully")}
                  </p>
                </div>
                <div className={"flex flex-row w-full"}>
                  <p className={"text-base text-secondary text-start"}>
                    <Trans
                      i18nKey="one_to_one.info_event_created"
                      values={{
                        date: dayjs(date).format("ddd DD MMMM"),
                        hour,
                        topic:
                          topicsId[
                            options[Number(category)]?.key as keyof TopicsWithId
                          ][Number(topic)].title,
                      }}
                      components={{
                        b: <b className="font-semibold" />,
                      }}
                    />
                  </p>
                </div>
              </div>
              <div className={"flex gap-2  w-full flex-row justify-center"}>
                <LoadingButton
                  loading={isLoading}
                  disabled={isLoading}
                  color={"primary"}
                  size={"lg"}
                  onClick={() => step == 3 && navigate(routes.agenda)}
                >
                  {t("btns.go_to_agenda")}
                </LoadingButton>
              </div>
            </>
          )}
          {step !== 0 && step !== 3 && (
            <div className={"flex flex-row justify-center gap-2 w-full"}>
              <Button
                size={"md"}
                onClick={() => {
                  if (step == 0 || (step === 1 && selectedPreviousTutor)) {
                    setSecondSession(false);
                  } else {
                    setStep((r) => r - 1);
                    setState("topic", "");
                    setState("category", "");
                  }
                }}
                color={"white"}
              >
                {t("btns.back")}
              </Button>
              <LoadingButton
                size={"md"}
                disabled={
                  (step == 1 && !category) ||
                  (step === 2 && !topic) ||
                  (credits == 0 && step == 2)
                }
                onClick={() => {
                  if (step == 2) {
                    setState("topic", topic);
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    onBook();
                  } else {
                    setState("category", category);
                    setStep((r) => r + 1);
                  }
                }}
                color={step === 2 ? "green" : "primary"}
                loading={step >= 1 && isLoading}
              >
                {step === 2 ? t("btns.confirm") : t("btns.next")}
              </LoadingButton>
            </div>
          )}
        </OneToOneSectionLayout>
      </div>
    </div>
  );
};

export default DesktopSecondSessionView;
